'use client';

import React, { useEffect, useRef, useState } from 'react';
import { MapPinIcon } from '@heroicons/react/24/solid';
import { MapIcon } from '@heroicons/react/24/outline';

const GOOGLE_MAPS_EMBED_API_KEY =
  process.env.NEXT_PUBLIC_GOOGLE_MAP_REACT_KEY || 'INSERT_GOOGLE_MAPS_EMBED_API_KEY';

// Cuadrícula de calles de fondo mientras el mapa no está: ocupa el mismo alto (sin salto) y no pide
// nada a Google.
const PLACEHOLDER_STYLE = {
  backgroundColor: '#eef2f7',
  backgroundImage:
    'linear-gradient(rgba(255,255,255,.9) 2px, transparent 2px), linear-gradient(90deg, rgba(255,255,255,.9) 2px, transparent 2px), linear-gradient(rgba(255,255,255,.55) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.55) 1px, transparent 1px)',
  backgroundSize: '96px 96px, 96px 96px, 24px 24px, 24px 24px',
  backgroundPosition: 'center'
};

/** Link de «Cómo llegar» de Google Maps: coordenadas si las hay; si no, la dirección. */
export function directionsHref({ address, latitude, longitude }) {
  const destination =
    Number.isFinite(latitude) && Number.isFinite(longitude) ? `${latitude},${longitude}` : address || '';
  return destination
    ? `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(destination)}`
    : null;
}

/**
 * Mapa de ubicación (perfil, detalle de servicio y ficha del directorio). El embed de Google pesa
 * ~1 MB y demoraba el `load` de la ficha (~1,2 s de 1,5 s en local): ahora se muestra una
 * cuadrícula del mismo alto y el mapa interactivo se carga cuando la tarjeta entra en pantalla
 * (recién después del `load` de la página) o al tocar «Ver mapa».
 */
const Map = ({ height = '320px', address, latitude, longitude }) => {
  const [showMap, setShowMap] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    if (showMap) return undefined;
    const node = containerRef.current;
    if (!node || typeof IntersectionObserver === 'undefined') return undefined;
    let observer;
    const observe = () => {
      observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            setShowMap(true);
            observer.disconnect();
          }
        },
        { rootMargin: '0px 0px 120px 0px', threshold: 0.15 }
      );
      observer.observe(node);
    };
    if (document.readyState === 'complete') {
      observe();
      return () => observer?.disconnect();
    }
    window.addEventListener('load', observe, { once: true });
    return () => {
      window.removeEventListener('load', observe);
      observer?.disconnect();
    };
  }, [showMap]);

  const iframeSrc = address
    ? `https://www.google.com/maps/embed/v1/place?key=${GOOGLE_MAPS_EMBED_API_KEY}&q=${encodeURIComponent(address)}&zoom=15`
    : `https://www.google.com/maps/embed/v1/view?key=${GOOGLE_MAPS_EMBED_API_KEY}&center=${latitude},${longitude}&zoom=15`;
  const directions = directionsHref({ address, latitude, longitude });

  return (
    <div ref={containerRef} className="rounded-2xl overflow-hidden border border-gray-100 bg-gray-50">
      <div className="relative w-full" style={{ height }}>
        {showMap ? (
          <iframe
            title="Mapa de ubicación"
            width="100%"
            height="100%"
            style={{ border: 0 }}
            loading="lazy"
            allowFullScreen
            referrerPolicy="no-referrer-when-downgrade"
            src={iframeSrc}
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center" style={PLACEHOLDER_STYLE}>
            <MapPinIcon
              className="absolute left-1/2 top-1/2 h-10 w-10 -translate-x-1/2 -translate-y-[85%] text-primary-500 drop-shadow"
              aria-hidden="true"
            />
            <button
              type="button"
              onClick={() => setShowMap(true)}
              className="absolute bottom-4 left-1/2 inline-flex min-h-11 -translate-x-1/2 items-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-2 text-[14px] font-semibold text-[#1a1a1a] shadow-sm transition-colors hover:bg-gray-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500"
            >
              <MapIcon className="h-[18px] w-[18px]" aria-hidden="true" />
              Ver mapa
            </button>
          </div>
        )}
      </div>
      {(address || directions) && (
        <div className="flex items-center justify-between gap-3 px-4 py-3 bg-white border-t border-gray-100">
          {address && <p className="min-w-0 text-[13px] text-gray-500 font-medium">{address}</p>}
          {directions && (
            <a
              href={directions}
              target="_blank"
              rel="noopener noreferrer"
              className="ml-auto inline-flex flex-shrink-0 items-center gap-1.5 text-[13px] font-semibold text-primary-600 hover:underline"
            >
              <MapIcon className="h-4 w-4" aria-hidden="true" />
              Cómo llegar
            </a>
          )}
        </div>
      )}
    </div>
  );
};

export default Map;
